import { vuexOidcCreateStoreModule } from "vuex-oidc";
import router from "@/core/router/router";
import store from "@/core/store";
import SuperTokensLock from "browser-tabs-lock";
import getEnv from "@/core/utils/env";
import { sleep } from "@/core/utils/utils";
const superTokensLock = new SuperTokensLock();
import { oidcChannel } from "@/core/broadcast/broadcast";

const oidcSettings = {
  authority: getEnv("VUE_APP_OIDC_AUTHORITY"),
  client_id: getEnv("VUE_APP_OIDC_CLIENT_ID"),
  redirect_uri: getEnv("VUE_APP_BASE_WEB") + getEnv("VUE_APP_OIDC_REDIRECT_URI"),
  responseType: getEnv("VUE_APP_OIDC_RESPONSE_TYPE"),
  scope: getEnv("VUE_APP_OIDC_SCOPE"),
  automaticSilentRenew: getEnv("VUE_APP_OIDC_AUTOMATIC_SILENT_RENEW") == "true"
};

oidcChannel.onmessage = async (msg: MessageEvent<string>) => {
  console.log("Broadcast msg received: ", msg.data);

  switch (msg.data) {
    case "user-login-success": {
      // User logged in using different tab - refresh auth status for current tab
      await store.dispatch("getOidcUser").then(async (user: any) => {
        await store.dispatch("oidcWasAuthenticated", user);
        await store.dispatch("GetConfig");
      });
      break;
    }
    case "silent-token-refresh-success": {
      // User refreshed access token using different tab - refresh auth status for current tab
      await store.dispatch("getOidcUser").then(async (user: any) => {
        await store.dispatch("oidcWasAuthenticated", user);
      });
      break;
    }
    case "logout-triggered": {
      // User logged out using different tab - refresh auth status for current tab and redirect to Session Expired screen
      await store.dispatch("removeOidcUser");
      await router.push({ name: "SessionExpired" });
      await store.dispatch("GetConfig");
      break;
    }
    case "activate-tenant-initialized": {
      await router.push({ name: "SessionExpired" }).then(async () => {
        await store.dispatch("removeOidcUser");
        await store.dispatch("GetConfig");
      });
      break;
    }
    default: {
      console.warn("Unhandled broadcast message!");
    }
  }
};

export default vuexOidcCreateStoreModule(
  oidcSettings,
  { publicRoutePaths: ["/", "/session-expired", "/authentication-error", "/activate-tenant"] },
  {
    userLoaded: user => {
      console.log("OIDC userLoaded:", user);
    },
    userUnloaded: () => {
      console.log("OIDC userUnloaded");
    },
    accessTokenExpiring: () => {
      (async () => {
        console.log("OIDC accessTokenExpiring");
        console.log("Access token expiring. Trying to acquire silent renew lock...");

        if (await superTokensLock.acquireLock("silent-renew-lock", 500)) {
          console.log("Lock Acquired! Executing Silent Authentication...");

          try {
            await store.dispatch("authenticateOidcSilent").then(() => {
              oidcChannel.postMessage("silent-token-refresh-success");
            });
            await sleep(5000);
          } catch (e) {
            console.error(e);
          } finally {
            await superTokensLock.releaseLock("silent-renew-lock");
          }
        } else {
          console.log("Failed to acquire lock! Skipping Silent Authentication...");
        }
      })().catch(e => console.error("OIDC Expiring accessToken failed", e));
    },
    accessTokenExpired: () => {
      (async () => {
        console.log("OIDC accessTokenExpired");
        await store.dispatch("removeOidcUser");
        await router.push({ name: "SessionExpired" });
      })().catch(e => console.error("OIDC accessTokenExpire failed", e));
    },
    silentRenewError: () => {
      (async () => {
        console.log("OIDC silentRenewError");
        await store.dispatch("removeOidcUser");
        await router.push({ name: "AuthenticationError" });
      })().catch(e => console.error("Unable to Renew Error Silently", e));
    },
    userSignedOut: () => {
      (async () => {
        console.log("OIDC userSignedOut");
        await store.dispatch("removeOidcUser");
        await router.push({ name: "SessionExpired" });
      })().catch(e => console.error("Unable to Sign out User", e));
    },
    oidcError: payload => {
      (async () => {
        console.log(`OIDC oidcError at ${payload?.context}:`, payload?.error);
        await store.dispatch("removeOidcUser");
        await router.push({ name: "AuthenticationError" });
      })().catch(e => console.error("OIDC Error", e));
    },
    automaticSilentRenewError: payload => {
      (async () => {
        console.log("OIDC automaticSilentRenewError: ", payload?.error);
        await router.push({ name: "AuthenticationError" });
      })().catch(e => console.error("Unable to Renew Error automatically in silent", e));
    }
  }
);
